/**
 * useShellEffectPresets - the table's shared effect palette (v8.199.2; any kind since v8.370.1).
 *
 * Copy-on-attach: combat never reads it. Listens to the session's presets,
 * sorted by name, and saves new ones under a time-based id. A row without a
 * kind was written before v8.370.1: an affliction. A corrupt preset is
 * skipped rather than losing the rest. Extracted from DeathWatchSimulator.
 */
import { useState, useEffect, useCallback } from 'react';
import * as multiplayer from '../multiplayer';
import { warnIfRejected } from '../diagnostics/log';

/** One stored row as a preset, or null when it will not parse or has no name. */
function presetOf(d) {
  try {
    const spec = JSON.parse(d.data.spec);
    return spec && spec.name ? { id: d.id, kind: d.data.kind || 'affliction', spec } : null;
  } catch {
    return null; // a corrupt preset is skipped rather than losing the rest
  }
}

/** The stored rows as presets, by name. */
export function presetsFrom(entries) {
  const next = entries.map(presetOf).filter(Boolean);
  next.sort((a, b) => a.spec.name.localeCompare(b.spec.name));
  return next;
}

/** @returns {{ effectPresets: object[], handleSaveEffectPreset: Function }} */
export default function useShellEffectPresets(sessionId, playerId) {
  const [effectPresets, setEffectPresets] = useState([]);
  useEffect(() => {
    if (!sessionId || !multiplayer.onEffectPresetsUpdate) return undefined;
    const unsub = multiplayer.onEffectPresetsUpdate(sessionId, ({ entries }) => setEffectPresets(presetsFrom(entries)));
    return () => unsub();
  }, [sessionId]);
  const handleSaveEffectPreset = useCallback(
    (preset) => {
      if (!sessionId) return;
      const save = multiplayer.setEffectPreset(sessionId, 'ep-' + Date.now().toString(36), preset, playerId);
      warnIfRejected(save, 'presets', 'effect preset save');
    },
    [sessionId, playerId],
  );
  return { effectPresets, handleSaveEffectPreset };
}
